<template>
  <div class="vehicle-config-page">
    <div class="page-header">
      <div class="header-left">
        <h2>签到牌号设置</h2>
        <p class="header-desc">根据线路类型、出发地区配置车辆自动编号规则。</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i> 添加配置
        </el-button>
      </div>
    </div>
    <div class="config-container">
      <!-- 搜索筛选 -->
      <div class="search-bar">
        线路类型：
        <el-select
          v-model="filters.routeProperty"
          placeholder="线路类型"
          class="search-select"
          @change="handleSearch"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in routeTypeList"
            :key="item.dictValue"
            :label="item.dictLabel"
            :value="item.dictValue"
          />
        </el-select>
        省份：
        <el-select
          v-model="filters.province"
          placeholder="省份"
          class="search-select"
          @change="handleSearch"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in provinceList"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
      </div>

      <!-- 表格 -->
      <el-table :data="configs" style="width: 100%" v-loading="loading" border>
        <el-table-column label="序号" width="60" type="index" />
        <el-table-column label="线路类型" width="200">
          <template slot-scope="scope">
            {{ scope.row.routeTypeLabel }}
          </template>
        </el-table-column>
        <el-table-column label="出发省" width="200">
          <template slot-scope="scope">
            {{ scope.row.provinceName }}
          </template>
        </el-table-column>
        <el-table-column label="出发城市" width="200">
          <template slot-scope="scope">
            {{ scope.row.cityName }}
          </template>
        </el-table-column>
        <el-table-column label="编号范围">
          <template slot-scope="scope">
            {{ scope.row.startNumber }} ~ {{ scope.row.endNumber }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleView(scope.row)">
              查看
            </el-button>
            <el-button type="text" @click="editConfig(scope.row)">
              编辑
            </el-button>
            <el-button type="text" @click="handleDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <span class="pagination-info"
          >显示第 {{ (currentPage - 1) * pageSize + 1 }} 至
          {{ Math.min(currentPage * pageSize, total) }} 条，共
          {{ total }} 条记录</span
        >
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 配置弹窗 -->
    <el-dialog
      :title="
        isViewMode
          ? '查看签到牌号设置'
          : currentConfig
          ? '编辑签到牌号设置'
          : '新增签到牌号设置'
      "
      :visible.sync="configDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form
        ref="configForm"
        :model="configForm"
        :rules="configRules"
        label-width="100px"
      >
        <el-form-item label="线路类型" prop="routeProperty">
          <!-- 查看模式直接显示文本 -->
          <template v-if="isViewMode">
            <span>{{ currentRouteTypeLabel }}</span>
          </template>
          <!-- 编辑模式使用选择框 -->
          <template v-else>
            <el-select
              v-model="configForm.routeProperty"
              placeholder="请选择线路类型"
              style="width: 100%"
            >
              <!-- 临时选项用于回显中文，在下拉列表中隐藏 -->
              <el-option
                v-for="item in routeTypeList"
                :key="item.dictValue"
                :label="item.dictLabel"
                :value="item.dictValue"
                :class="{ 'temp-option': item.isTemp }"
              />
            </el-select>
          </template>
        </el-form-item>
        <el-form-item label="出发省份" prop="provinceCode">
          <el-select
            v-model="configForm.provinceCode"
            placeholder="请选择出发省份"
            :disabled="isViewMode"
            style="width: 100%"
            @change="handleProvinceChange"
          >
            <el-option
              v-for="item in provinceList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="出发城市" prop="cityCode">
          <el-select
            v-model="configForm.cityCode"
            placeholder="请选择出发城市"
            style="width: 100%"
            :disabled="!configForm.provinceCode || isViewMode"
          >
            <el-option
              v-for="item in cityList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="起始编号" prop="startNumber">
          <el-input
            v-model="configForm.startNumber"
            :disabled="isViewMode"
            placeholder="如：1000"
          />
        </el-form-item>
        <el-form-item label="结束编号" prop="endNumber">
          <el-input
            v-model="configForm.endNumber"
            :disabled="isViewMode"
            placeholder="如：1999"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="configDialogVisible = false">取消</el-button>
        <el-button
          v-if="!isViewMode"
          type="primary"
          @click="confirmConfig"
          :loading="configLoading"
          >确认保存</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 验证结束编号大于起始编号
const validateEndNumber = (rule, value, callback) => {
  if (!value) {
    callback()
    return
  }
  const startNum = parseInt(this.configForm.startNumber)
  const endNum = parseInt(value)
  if (!isNaN(startNum) && !isNaN(endNum) && endNum <= startNum) {
    callback(new Error('结束编号必须大于起始编号'))
  } else {
    callback()
  }
}
export default {
  name: 'VehicleConfig',
  computed: {
    // 当前线路类型的显示标签
    currentRouteTypeLabel() {
      const routeProperty = this.configForm.routeProperty
      if (!routeProperty) return ''
      // 先从下拉列表中查找
      const found = this.routeTypeList.find(
        (item) => item.dictValue === routeProperty
      )
      if (found) {
        return found.dictLabel
      }
      // 如果不在选项中，使用临时标签
      return this.currentRouteTypeLabelTemp || routeProperty
    },
    // 当前线路类型值是否在有效选项中
    isRoutePropertyValid() {
      const routeProperty = this.configForm.routeProperty
      if (!routeProperty) return true
      return this.routeTypeList.some((item) => item.dictValue === routeProperty)
    }
  },
  data() {
    return {
      filters: {
        routeProperty: '',
        province: '',
        keyword: ''
      },
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      configs: [],
      configDialogVisible: false,
      configForm: {
        configId: null,
        routeProperty: '',
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        startNumber: '',
        endNumber: '',
        status: '1'
      },
      configRules: {
        routeProperty: [
          { required: true, message: '请选择线路类型', trigger: 'change' }
        ],
        provinceCode: [
          { required: true, message: '请选择出发省份', trigger: 'change' }
        ],
        cityCode: [
          { required: true, message: '请选择出发城市', trigger: 'change' }
        ],
        startNumber: [
          { required: true, message: '请输入起始编号', trigger: 'blur' },
          {
            validator: this.validateNumber,
            trigger: 'blur'
          }
        ],
        endNumber: [
          { required: true, message: '请输入结束编号', trigger: 'blur' },
          {
            validator: this.validateNumber,
            trigger: 'blur'
          },
          {
            validator: this.validateEndNumber,
            trigger: 'blur'
          }
        ],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      isViewMode: false,
      configLoading: false,
      currentConfig: null,
      // 下拉选项数据
      routeTypeList: [],
      currentRouteTypeLabelTemp: '',
      provinceList: [],
      cityList: []
    }
  },
  mounted() {
    this.fetchConfigs()
    this.loadProvinceList()
    this.loadRouteTypeList()
  },
  methods: {
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchConfigs()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchConfigs()
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchConfigs()
    },
    handleCreate() {
      this.currentConfig = null
      this.configForm = {
        configId: null,
        routeProperty: '',
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        startNumber: '',
        endNumber: '',
        status: '1'
      }
      this.cityList = []
      this.configDialogVisible = true
      this.$nextTick(() => {
        this.$refs.configForm && this.$refs.configForm.clearValidate()
      })
    },
    // 加载线路类型列表
    async loadRouteTypeList() {
      try {
        const res = await this.$http.post(
          'dict/getDictData.do?dictType=route_type'
        )
        if (res.code === '0' || res.code === 0) {
          this.routeTypeList = res.result || []
        } else {
          console.error('获取线路类型失败:', res.message)
        }
      } catch (error) {
        console.error('获取线路类型错误:', error)
      }
    },
    async editConfig(row) {
      this.currentConfig = row
      // 保存当前的routeTypeLabel用于回显
      this.currentRouteTypeLabelTemp = row.routeTypeLabel || ''
      try {
        const response = await this.$http.get(
          'vehicleNumber/getVehicleNumberConfigDetail.do',
          {
            params: { configId: row.configId }
          }
        )
        if (response.code === '0' || response.code === 0) {
          const data = response.result || response.data
          // 更新临时标签
          this.currentRouteTypeLabelTemp =
            data.routeTypeLabel || this.currentRouteTypeLabelTemp
          const routeProperty = data.routeProperty || ''

          // 如果线路类型值在下拉列表中没有匹配项，添加禁用的临时选项用于回显中文
          const hasMatch = this.routeTypeList.some(
            (item) => item.dictValue === routeProperty
          )
          if (!hasMatch && routeProperty && this.currentRouteTypeLabelTemp) {
            this.routeTypeList.push({
              dictValue: routeProperty,
              dictLabel: this.currentRouteTypeLabelTemp,
              isTemp: true // 标记为临时选项
            })
          }

          this.configForm = {
            configId: data.configId || null,
            routeProperty: routeProperty,
            provinceCode: data.provinceCode || '',
            provinceName: data.provinceName || '',
            cityCode: data.cityCode || '',
            cityName: data.cityName || '',
            startNumber: data.startNumber || '',
            endNumber: data.endNumber || '',
            status: data.status !== undefined ? String(data.status) : '1'
          }
          // 加载对应省份的城市列表
          if (this.configForm.provinceCode) {
            this.loadCityList(this.configForm.provinceCode)
          }
        }
      } catch (error) {
        console.error('获取配置详情失败:', error)
        this.$message.error('获取配置详情失败')
      }
      this.configDialogVisible = true
      this.$nextTick(() => {
        this.$refs.configForm && this.$refs.configForm.clearValidate()
      })
    },
    // 关闭对话框时清理临时添加的选项
    handleDialogClose() {
      this.currentRouteTypeLabelTemp = ''
      // 移除临时添加的线路类型选项
      this.routeTypeList = this.routeTypeList.filter((item) => !item.isTemp)
    },
    // 验证编号为正整数且在100000以内
    validateNumber(rule, value, callback) {
      if (!value) {
        callback()
        return
      }
      const num = parseInt(value)
      if (isNaN(num) || num <= 0) {
        callback(new Error('请输入正整数'))
      } else if (num > 100000) {
        callback(new Error('编号不能超过100000'))
      } else {
        callback()
      }
    },

    // 加载省份列表
    async loadProvinceList() {
      try {
        const response = await this.$http.get('/region/getAllProvinces.do')
        if (response.code === '0' || response.code === 0) {
          this.provinceList = response.result || []
        }
      } catch (error) {
        console.error('加载省份列表失败:', error)
        this.$message.error('加载省份列表失败')
      }
    },
    // 根据省份加载城市列表
    async loadCityList(provinceCode) {
      try {
        const response = await this.$http.get(
          '/region/getCitiesByProvince.do',
          {
            params: { provinceCode }
          }
        )
        if (response.code === '0' || response.code === 0) {
          this.cityList = response.result || []
        }
      } catch (error) {
        console.error('加载城市列表失败:', error)
        this.$message.error('加载城市列表失败')
      }
    },
    // 省份变化时加载城市列表
    handleProvinceChange(provinceCode) {
      this.configForm.cityCode = ''
      this.configForm.cityName = ''
      // 设置省份名称
      const province = this.provinceList.find((p) => p.code === provinceCode)
      if (province) {
        this.configForm.provinceName = province.name
      }
      if (provinceCode) {
        this.loadCityList(provinceCode)
      } else {
        this.cityList = []
      }
    },
    handleView(row) {
      this.isViewMode = true

      this.currentConfig = row
      this.configDialogVisible = true
      this.editConfig(row)
    },
    // 删除配置
    handleDelete(row) {
      this.$confirm('确定要删除该车辆编号配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          try {
            const response = await this.$http.get(
              'vehicleNumber/updateVehicleNumberConfigStatus.do',
              {
                params: { configId: row.configId }
              }
            )
            if (response.code === '0' || response.code === 0) {
              this.$message.success('删除成功')
              this.fetchConfigs()
            } else {
              this.$message.error(response.message || '删除失败')
            }
          } catch (error) {
            console.error('删除失败:', error)
            this.$message.error('删除失败')
          }
        })
        .catch(() => {
          this.$message.info('已取消删除')
        })
    },
    async fetchConfigs() {
      this.loading = true
      try {
        const response = await this.$http.get(
          'vehicleNumber/getVehicleNumberConfigList.do',
          {
            params: {
              pageNum: this.currentPage,
              pageSize: this.pageSize,
              routeProperty: this.filters.routeProperty,
              provinceCode: this.filters.province
            }
          }
        )
        if (response.code === '0' || response.code === 0) {
          this.configs = response.result.list || response.result || []
          this.total = response.result.total || this.configs.length
        } else {
          this.$message.error(response.message || '获取配置列表失败')
        }
      } catch (error) {
        console.error('获取配置列表错误:', error)
        this.$message.error('网络请求异常')
      } finally {
        this.loading = false
      }
    },
    async confirmConfig() {
      try {
        await this.$refs.configForm.validate()

        // 设置城市名称
        const city = this.cityList.find(
          (c) => c.code === this.configForm.cityCode
        )
        if (city) {
          this.configForm.cityName = city.name
        }

        this.configLoading = true
        let response
        if (this.configForm.configId) {
          // 修改
          response = await this.$http.post(
            'vehicleNumber/updateVehicleNumberConfig.do',
            this.configForm
          )
        } else {
          // 新增
          response = await this.$http.post(
            'vehicleNumber/addVehicleNumberConfig.do',
            this.configForm
          )
        }

        if (response.code === '0' || response.code === 0) {
          this.$message.success('配置保存成功')
          this.configDialogVisible = false
          this.fetchConfigs()
        } else {
          this.$message.error(response.message || '保存失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('保存配置错误:', error)
          this.$message.error(error.message || '网络请求异常')
        }
      } finally {
        this.configLoading = false
      }
    }
  }
}
</script>

<style scoped>
.vehicle-config-page {
  padding: 20px 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 80px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.header-desc {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #6b7280;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 20px;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.search-select {
  width: 140px;
}

.search-input {
  width: 260px;
}

.status-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-text {
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
}

.status-hint {
  font-size: 12px;
  color: #9ca3af;
}

.range-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.range-separator {
  color: #9ca3af;
  font-size: 16px;
  font-weight: bold;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.pagination-info {
  font-size: 13px;
  color: #6b7280;
}

.pagination .el-pagination {
  margin: 0;
}

.pagination .el-pagination button {
  border-radius: 6px;
}

.pagination .el-pagination .el-pager li.active {
  background-color: #22c55e;
  border-color: #22c55e;
  color: #fff;
}

.pagination .el-pagination .el-pager li.active:hover {
  background-color: #16a34a;
}

/* 隐藏临时选项在下拉列表中 */
.temp-option {
  display: none !important;
}
</style>
